import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Image, Download } from 'lucide-react'
import { api } from '../../api/client'
import Button from '../../components/ui/Button'
import { Select, Textarea } from '../../components/ui/Input'

const OPENAI_MODELS = [
  { value: 'dall-e-3', label: 'DALL-E 3' },
  { value: 'dall-e-2', label: 'DALL-E 2' },
]

const GEMINI_MODELS = [
  { value: 'imagen-3.0-generate-002', label: 'Imagen 3' },
  { value: 'gemini-2.0-flash-preview-image-generation', label: 'Gemini Flash' },
]

const OPENAI_SIZES = [
  { value: '1024x1024', label: '1024x1024' },
  { value: '1024x1792', label: '1024x1792' },
  { value: '1792x1024', label: '1792x1024' },
]

const GEMINI_SIZES = [
  { value: '1024x1024', label: '1024x1024' },
  { value: '1152x896', label: '1152x896' },
  { value: '896x1152', label: '896x1152' },
]

const QUALITY_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'hd', label: 'HD' },
]

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Gemini' },
]

export default function ImagePage() {
  const [form, setForm] = useState({
    prompt: '',
    provider: 'openai',
    model: 'dall-e-3',
    size: '1024x1024',
    quality: 'standard',
  })

  const mutation = useMutation({
    mutationFn: (data: typeof form) => api.post<any>('/image', data),
  })

  const { data: galleryData } = useQuery({
    queryKey: ['images'],
    queryFn: () => api.get<{ images: any[] }>('/images?limit=20'),
  })

  const modelOptions = form.provider === 'openai' ? OPENAI_MODELS : GEMINI_MODELS
  const sizeOptions = form.provider === 'openai' ? OPENAI_SIZES : GEMINI_SIZES

  const handleProviderChange = (provider: string) => {
    setForm(f => ({
      ...f,
      provider,
      model: provider === 'openai' ? 'dall-e-3' : 'imagen-3.0-generate-002',
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate(form)
  }

  const images = mutation.data?.images || []
  const gallery = galleryData?.images || []

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Image size={20} className="text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Image Generator</h1>
          <p className="text-sm text-gray-500">Generate marketing images and visuals with AI</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Textarea
              label="Prompt"
              value={form.prompt}
              onChange={(e) => setForm(f => ({ ...f, prompt: e.target.value }))}
              required
              placeholder="Describe the image you want to generate..."
            />
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Provider"
                options={PROVIDER_OPTIONS}
                value={form.provider}
                onChange={(e) => handleProviderChange(e.target.value)}
              />
              <Select
                label="Model"
                options={modelOptions}
                value={form.model}
                onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))}
              />
              <Select
                label="Size"
                options={sizeOptions}
                value={form.size}
                onChange={(e) => setForm(f => ({ ...f, size: e.target.value }))}
              />
              {form.provider === 'openai' && (
                <Select
                  label="Quality"
                  options={QUALITY_OPTIONS}
                  value={form.quality}
                  onChange={(e) => setForm(f => ({ ...f, quality: e.target.value }))}
                />
              )}
            </div>
            <Button type="submit" loading={mutation.isPending} className="w-full">
              <Image size={16} /> Generate
            </Button>
            {mutation.error && (
              <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                {(mutation.error as Error).message}
              </div>
            )}
          </form>
        </div>

        <div>
          {mutation.isPending && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center justify-center text-gray-400">
              <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm">Generating image...</p>
            </div>
          )}
          {images.length > 0 && !mutation.isPending ? (
            <div className="space-y-4">
              {images.map((img: any, i: number) => (
                <div key={i} className="relative group">
                  <img
                    src={img.url}
                    alt={img.revised_prompt || 'Generated image'}
                    className="w-full rounded-xl border border-gray-200"
                  />
                  <a
                    href={img.url}
                    download
                    className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Download size={16} />
                  </a>
                  {img.revised_prompt && (
                    <p className="text-xs text-gray-500 mt-2 italic">{img.revised_prompt}</p>
                  )}
                </div>
              ))}
            </div>
          ) : !mutation.isPending ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center text-gray-400">
              <Image size={32} className="mb-3 opacity-30" />
              <p className="text-sm">Generated images will appear here</p>
            </div>
          ) : null}
        </div>
      </div>

      {gallery.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Recent Images</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {gallery.map((img: any, i: number) => (
              <img
                key={i}
                src={img.url || `/api/images/${img.filename}`}
                alt=""
                className="w-full aspect-square object-cover rounded-xl border border-gray-200 hover:opacity-80 cursor-pointer transition-opacity"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
